import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';

async function checkAdminRole(userId: string | undefined): Promise<boolean> {
  if (!userId) return false;

  try {
    const { data: roles, error } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', userId)
      .eq('role', 'admin');

    if (error) {
      console.error('Error checking admin role:', error);
      return false;
    }

    return roles && roles.length > 0;
  } catch (error) {
    console.error('Error in admin check:', error);
    return false;
  }
}

export function useAdminRole(redirectOnFail = true) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [userId, setUserId] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUserId(session?.user?.id ?? null);
      queryClient.invalidateQueries({ queryKey: ['adminRole'] });
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      setUserId(session?.user?.id ?? null);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [queryClient]);

  // Use React Query with proper caching - staleTime prevents refetches
  const { data: isAdmin = false, isLoading: isRoleLoading } = useQuery({
    queryKey: ['adminRole', userId],
    queryFn: async () => {
      return checkAdminRole(userId ?? undefined);
    },
    enabled: userId !== undefined,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 10 * 60 * 1000,   // Keep in cache for 10 minutes
  });

  const isLoading = userId === undefined || isRoleLoading;

  // Handle redirect
  useEffect(() => {
    if (!isLoading && !isAdmin && redirectOnFail) {
      // If unauthenticated, send to /auth so the user can sign in and come back to /admin.
      // Otherwise (signed in but not admin), send to home.
      if (!userId) {
        const here = window.location.pathname + window.location.search;
        navigate(`/auth?redirect=${encodeURIComponent(here)}`, { replace: true });
      } else {
        navigate('/');
      }
    }
  }, [isAdmin, isLoading, redirectOnFail, navigate, userId]);

  return { isAdmin, isLoading };
}
